import React, { useMemo } from "react";

import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook } from "hooks";
import { useDateFormatSelect } from "store";

import { border, cellPx, templateColumns as cols, s } from "../styles";
import { FilePickerSelectionReturn } from "../hooks/use-file-picker-selection";

const headerSx = {
  fontSize: 14,
  fontWeight: 500,
  color: "#071F35",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...cellPx
};
const cellSx = { fontSize: 14, color: "#071F35", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

interface Props {
  selection: FilePickerSelectionReturn;
  investorMap: Map<number, string>;
}

export const ListView: React.FC<Props> = ({ selection, investorMap }) => {
  const formatDate = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const sortedFiles = useMemo(() => {
    const filtered = selection.resultFiles.filter((f) => selection.matchesSearch(f) && selection.matchesFilters(f));
    return [...filtered].sort((a, b) => {
      if (!a.created_at && !b.created_at) return 0;
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      return a.created_at < b.created_at ? 1 : -1;
    });
  }, [selection.resultFiles, selection.matchesSearch, selection.matchesFilters]);

  const isFiltering = !!selection.searchQuery || selection.hasActiveFilters;

  if (sortedFiles.length === 0) {
    return (
      <Box sx={{ py: "56px", px: "20px", textAlign: "center" }}>
        <SearchOffIcon sx={{ fontSize: 32, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
        {isFiltering ? (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No files match the current filters.</Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No files in workspace</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ border: border(), borderRadius: "8px", mx: "20px", my: "16px" }}>
      {/* Sticky header */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.default",
          borderBottom: border(),
          height: 32,
          borderRadius: "8px 8px 0 0"
        }}
      >
        <Box sx={{ ...cols.checkbox }} />
        <MaterialTooltip variant="dark" title="Name" placement="top">
          <Typography sx={{ ...cols.name, ...headerSx }}>Name</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Altius Classification" placement="top">
          <Typography sx={{ ...cols.classification, ...headerSx }}>Altius Classification</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Investor" placement="top">
          <Typography sx={{ ...cols.investor, ...headerSx }}>Investor</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Effective date" placement="top">
          <Typography sx={{ ...cols.effectiveDate, ...headerSx }}>Effective date</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Upload date" placement="top">
          <Typography sx={{ ...cols.uploadDate, ...headerSx }}>Upload date</Typography>
        </MaterialTooltip>
      </Stack>

      {/* File rows */}
      {sortedFiles.map((file) => {
        const selected = selection.isFileSelected(file.id);
        const disabled = selection.isSelectionDisabled(file.id);
        const investorName = file.investor_id ? investorMap.get(file.investor_id) : null;
        return (
          <Stack
            key={file.id}
            direction="row"
            alignItems="center"
            onClick={() => !disabled && selection.toggleFile(file.id, file.section_id)}
            sx={{ ...(selected ? s.fileRowSelected : s.fileRow), height: 32 }}
            tabIndex={0}
          >
            <Box
              sx={{
                ...cols.checkbox,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Checkbox
                checked={selected}
                disabled={disabled}
                onChange={() => selection.toggleFile(file.id, file.section_id)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0 }}
              />
            </Box>
            <Box
              sx={{
                ...cols.name,
                display: "flex",
                alignItems: "center",
                ...cellPx,
                overflow: "hidden"
              }}
            >
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: "#071F35", mr: "6px", flexShrink: 0 }} />
              <MaterialTooltip variant="dark" title={file.name} placement="top">
                <Typography noWrap sx={{ ...cellSx, color: "#071F35" }}>
                  {file.name}
                </Typography>
              </MaterialTooltip>
            </Box>
            <Box sx={{ ...cols.classification, ...cellPx, overflow: "hidden" }}>
              {file.document_type ? (
                <MaterialTooltip variant="dark" title={file.document_type} placement="top">
                  <Typography noWrap sx={cellSx}>
                    {file.document_type}
                  </Typography>
                </MaterialTooltip>
              ) : (
                <Typography sx={{ fontSize: 14, color: "text.disabled", fontStyle: "italic" }}>{"\u2014"}</Typography>
              )}
            </Box>
            <Box sx={{ ...cols.investor, ...cellPx, overflow: "hidden" }}>
              {investorName ? (
                <MaterialTooltip variant="dark" title={investorName} placement="top">
                  <Typography noWrap sx={cellSx}>
                    {investorName}
                  </Typography>
                </MaterialTooltip>
              ) : (
                <Typography sx={{ fontSize: 14, color: "text.disabled" }}>{"\u2014"}</Typography>
              )}
            </Box>
            <Box sx={{ ...cols.effectiveDate, ...cellPx }}>
              <MaterialTooltip variant="dark" title={formatDate({ date: file.date, format: dateFormat })} placement="top">
                <Typography noWrap sx={cellSx}>
                  {formatDate({ date: file.date, format: dateFormat })}
                </Typography>
              </MaterialTooltip>
            </Box>
            <Box sx={{ ...cols.uploadDate, ...cellPx }}>
              <MaterialTooltip variant="dark" title={formatDate({ date: file.created_at, format: dateFormat })} placement="top">
                <Typography noWrap sx={cellSx}>
                  {formatDate({ date: file.created_at, format: dateFormat })}
                </Typography>
              </MaterialTooltip>
            </Box>
          </Stack>
        );
      })}
    </Box>
  );
};
